import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import type { PersistedClient, Persister } from "@tanstack/react-query-persist-client";
import { localForageStorage } from "./storage";

const storagePersister = createAsyncStoragePersister({
  storage: localForageStorage,
  key: "cliip:query-cache",
});

// isInvalidated counts too: a type invalidated without an observer has to be stored as such to refetch after a reload.
const getFingerprint = ({ clientState }: PersistedClient): string =>
  clientState.queries
    .map(({ queryHash, state }) => `${queryHash}:${state.dataUpdatedAt}:${state.isInvalidated}`)
    .join("|");

let lastFingerprint: string | undefined;

/**
 * The provider saves on every event of the whole query cache - a table page, a poll - so without the fingerprint
 * check every second of work would serialize all enumerations again, although they change about once an hour.
 * Outside the provider so that an account switch in another tab can clear the stored cache before a reload.
 */
export const queryPersister: Persister = {
  persistClient: (persistedClient) => {
    const fingerprint = getFingerprint(persistedClient);

    if (fingerprint === lastFingerprint) {
      return;
    }

    lastFingerprint = fingerprint;

    return storagePersister.persistClient(persistedClient);
  },
  restoreClient: storagePersister.restoreClient,
  removeClient: () => {
    lastFingerprint = undefined;

    return storagePersister.removeClient();
  },
};
